<template>
  <h1 id="privacy-policy">Privacy Policy</h1>
  <h2 id="1-who-is-the-data-controller">1. Who is the Data Controller?</h2>
  <p>
    The entity responsible for processing your personal data is
    <strong>MAPPREXPLORE, S.L.</strong> (hereinafter, “<strong>MAPPR</strong>”),
    with N.I.F. B56479728 and its registered office at Plaça Vicens Martorell 2,
    PB L 2, 08001, Barcelona, Spain.
  </p>
  <p>
    Please note that the ownership of the website
    <a href="https://mappr.com/" target="_blank" class="url"
      >https://mappr.com/</a
    >
    (hereinafter, “<strong>Website</strong>”), belongs to MAPPR.
  </p>
  <p>
    This Privacy Policy regulates access to and use of the Website made
    available by MAPPR to internet users (hereinafter, the
    “<strong>User</strong>” or “<strong>Users</strong>”) interested in the
    content (hereinafter, the “<strong>Content</strong>”) and services
    (hereinafter, the “<strong>Services</strong>”) offered by MAPPR on the
    Website.
  </p>
  <p>
    Access to and use of the services offered through this site is restricted to
    individuals of legal age. In exceptional cases where the processing of
    personal data of children under the age of 14 is necessary, such processing
    shall be subject to the express prior consent of their legal guardians. If
    you are underage and do not fully understand this notice, we recommend that
    you seek assistance from your legal guardians.
  </p>
  <p>
    The User may contact MAPPR at the following email address:
    <a href="mailto:rgpd@mappr.com">rgpd@mappr.com</a>
  </p>
  <h2 id="2-recommendations">2. Recommendations</h2>
  <p>Please read and follow the recommendations below carefully:</p>
  <ul>
    <li>
      Keep your computer equipped with properly updated antivirus software
      against malicious software and spyware applications that may compromise
      your Internet browsing and the information stored on your computer.
    </li>
    <li>
      Read and review this Privacy Policy and all legal texts made available by
      MAPPR on the Website.
    </li>
  </ul>
  <h2 id="3-information-collected-by-mappr-through-the-website">
    3. Information collected by MAPPR through the Website
  </h2>
  <p>
    To ensure the proper functioning of the Website, MAPPR may access the
    following types of data provided, where appropriate, by the User:
  </p>
  <ol start="">
    <li>
      <strong>Identification and contact details</strong>: name, surname,
      address, postcode, telephone, email and country.
    </li>
    <li>
      <strong>Financial data</strong>: cardholder data, bank details and bank
      cards.
    </li>
    <li>
      <strong>Identification data</strong>: full name, national ID card
      (DNI/NIE/passport).
    </li>
    <li>
      <strong>Contact data</strong>: email address, phone number, postal
      address.
    </li>
    <li>
      <strong>Browsing data</strong>: IP address, device type, operating system,
      browser, language, pages visited, browsing time, and cookies or similar
      technologies (as per MAPPR’s
      <RouterLink to="/info/cookie-policy" class="url">Cookie Policy</RouterLink
      >).
    </li>
    <li>
      <strong>Interaction data</strong>: information provided through contact
      forms, queries, complaints or communications with MAPPR.
    </li>
    <li>
      <strong>Administrative and billing data</strong>: where applicable,
      banking or billing details required for managing services, payments, or
      refunds.
    </li>
    <li>
      <strong>Other data</strong>: additional information provided by the User
      via forms, surveys, or communications with MAPPR.
    </li>
  </ol>
  <p>&nbsp;</p>
  <h2 id="4-information-about-the-data-mappr-collects-through-the-website">
    4. Information about the data MAPPR collects through the Website
  </h2>
  <p>
    The Website uses cookies so that, depending on the configuration of the
    cookies, Mappr may process your personal data.
  </p>
  <p>
    Generally, the information we collect includes the IP address of the device
    being used, the browser being used, the access time and date and your
    operating system.
  </p>
  <p>
    You can find more information about this in our
    <RouterLink to="/info/cookie-policy" class="url">Cookie Policy</RouterLink>.
  </p>
  <h2 id="5-what-are-the-purposes-of-the-processing-of-your-personal-data">
    5. What are the purposes of the processing of your personal data?**
  </h2>
  <p>
    Your personal data collected by MAPPR may be used for the following
    purposes:
  </p>
  <ol>
    <li>To register you as a user of the website/platform.</li>
    <li>
      In case you request information through the forms or contact customer
      service, to provide you with the best service.
    </li>
    <li>
      To address information requests, complaints, claims, and technical
      support.
    </li>
    <li>Compliance with legal obligations.</li>
    <li>
      Depending on your cookie settings, to conduct statistical analyses and
      create profiles based on your browsing habits
    </li>
    <li>
      To send you newsletters, updates, and new content related to the platform,
      in accordance with your preferences and consent.
    </li>
  </ol>
  <h2 id="6-what-is-the-legal-basis-for-processing-your-data">
    6. What is the legal basis for processing your data?
  </h2>
  <p>
    The legal basis for processing Users’ personal data through the Website may
    vary depending on the purpose of the processing:
  </p>
  <ol start="">
    <li>
      In the case that you register as a user of the website, your data will be
      processed based on the execution of the contract and/or pre-contractual
      measures.
    </li>
    <li>
      In the case that you request information through the contact form or get
      in touch with our customer service; we will rely on the execution of the
      contract and/or pre-contractual measures.
    </li>
    <li>
      To address your requests for information, complaints, claims, or technical
      support, we will rely on the execution of pre-contractual measures.
    </li>
    <li>
      For the fulfilment of the various obligations in fiscal and accounting
      matters.
    </li>
    <li>
      If applicable, consent for conducting statistical analyses and creating
      profiles based on your browsing habits. Consent must be free, unequivocal,
      specific, and informed.
    </li>
  </ol>
  <p>
    By accepting this Privacy Policy and checking the corresponding boxes, the
    User agrees to the processing of their personal data.
  </p>
  <p>
    MAPPR takes the privacy and protection of Users’ personal data very
    seriously. For this reason, data are stored securely and processed with the
    utmost care, in compliance with
    <strong>Regulation (EU) 2016/679 of 27 April 2016</strong> (the General Data
    Protection Regulation – “GDPR”).
  </p>
  <p>
    This Privacy Policy governs access to and use of the service that MAPPR
    makes available to the User interested in the services and content hosted on
    the Website.
  </p>
  <p>
    Regarding this point, Users may withdraw their consent at any time by
    sending an email to <a href="mailto:rgpd@mappr.com">rgpd@mappr.com</a>,
    without affecting the lawfulness of processing based on consent before its
    withdrawal.
  </p>
  <h2 id="7-truthfulness-of-the-data-provided-by-users">
    7. Truthfulness of the data provided by Users
  </h2>
  <p>
    The User guarantees the accuracy of the personal data provided and
    undertakes to notify MAPPR of any changes to said data.
  </p>
  <p>
    In all cases, the User shall be solely responsible for the accuracy of the
    data provided, and MAPPR reserves the right to exclude any User who has
    provided false data from access to services, without prejudice to any legal
    actions that may apply.
  </p>
  <p>
    Users are advised to protect their data diligently, using appropriate
    security tools. MAPPR shall not be held liable for any theft, unlawful
    modification, or loss of data.
  </p>
  <p>
    Any modification or update of the data must be communicated to MAPPR through
    the means of contact set out in this Privacy Policy.
  </p>
  <h2 id="8-data-retention">8. Data retention</h2>
  <p>
    Protecting the privacy and personal data of Users is a top priority for
    MAPPR. Accordingly, MAPPR makes every effort to ensure that User data are
    not misused. Only authorised personnel have access to User data.
  </p>
  <p>
    User personal data will be retained as long as the User’s account with MAPPR
    remains active for the purposes for which the data were collected, unless
    the User withdraws consent, requests deletion of their account, or the
    erasure of certain personal data from the Website.
  </p>
  <p>
    Subsequently, and where necessary, MAPPR will keep the information duly
    blocked for the legally prescribed periods.
  </p>
  <p>
    Personal Data obtained through your express consent for the sending of
    commercial communications and/or newsletters will be kept until you revoke
    your consent and request to unsubscribe from the service.
  </p>
  <p>
    We may retain some personal data to comply with our legal or regulatory
    obligations, as well as to manage our rights (e.g., to enforce our claims
    before the Courts) or for statistical or historical purposes.
  </p>
  <p>
    MAPPR undertakes to comply with its duty of confidentiality regarding
    personal data, in accordance with applicable legislation.
  </p>
  <h2 id="9-user-rights">9. User rights</h2>
  <p>Users have the right to:</p>
  <ol start="">
    <li>Access their personal data;</li>
    <li>Request the rectification of inaccurate data;</li>
    <li>Request the deletion of their data;</li>
    <li>Request the restriction of the processing of their data;</li>
    <li>Object to the processing of their data;</li>
    <li>Request data portability;</li>
    <li>Not be subject to a decision based solely on automated processing.</li>
    <li>Right to withdraw consent</li>
  </ol>
  <p>
    Users may exercise these rights by contacting:
    <a href="mailto:rgpd@mappr.com">rgpd@mappr.com</a>, indicating the reason
    for their request.
  </p>
  <p>
    Alternatively, Users may send their request by post to: Plaça Vicens
    Martorell 2, PB L 2, 08001, Barcelona, Spain.
  </p>
  <p>The communication addressed to MAPPR must include:</p>
  <ul>
    <li>The User’s full name;</li>
    <li>A specific description of the request.</li>
  </ul>
  <p>
    Without prejudice to any other administrative remedy or judicial action, the
    User shall have the right to lodge a complaint with a Spanish Supervisory
    Authority. The supervisory authority shall inform the complainant about the
    course and outcome of the complaint.
  </p>
  <h2 id="10-data-security">10. Data security</h2>
  <p>
    MAPPR takes privacy and personal data protection very seriously. It has
    adopted all reasonable technical and organisational measures to prevent
    misuse of User data and to ensure that only authorised personnel have access
    to such data.
  </p>
  <p>
    MAPPR implements adequate security measures in accordance with applicable
    data protection law and has deployed all technical means at its disposal to
    prevent loss, misuse, alteration, unauthorised access, or theft of the data
    provided by the User through the Website, without prejudice to informing you
    that security measures on the Internet are not impregnable.
  </p>
  <p>
    MAPPR also undertakes to maintain confidentiality regarding personal data in
    accordance with applicable legislation.
  </p>
  <p>
    MAPPR shall not be liable for damages that may arise from the User’s failure
    to comply with their obligations.
  </p>
  <h2 id="11-transfers-andor-assignments-to-third-parties">
    11. Transfers and/or assignments to third parties
  </h2>
  <p>
    Your Personal Data may be processed by third-party service providers where
    necessary to fulfil the purposes outlined in this Privacy Policy, such as
    the provision of hosting services. Likewise, your data may be disclosed to
    competent authorities, courts, tribunals, or other authorised third parties
    where required to comply with applicable laws and regulations.
  </p>
  <p>
    The categories of third parties with whom we may share your personal data
    include:
  </p>
  <ul>
    <li>
      <strong>IT Service Providers</strong>: We may share your data with third
      parties who help us with hosting services, database maintenance, software
      applications, and other related services.
    </li>
    <li>
      <strong>CRM and Marketing Providers</strong>: Your data may be shared with
      third parties who assist us in customer relationship management (CRM),
      marketing, and digital campaigns.
    </li>
    <li>
      <strong>Web Analytics and Digital Media Agencies</strong>: Third parties
      who assist in analyzing the effectiveness of our digital content and
      advertising efforts may have access to your data.
    </li>
    <li>
      <strong>Legal Compliance</strong>: Your data may be disclosed to third
      parties when required for legal compliance, such as to meet obligations
      under applicable laws, regulations, or legal processes.
    </li>
  </ul>
  <p><strong>List of Third-Party Providers (if applicable):</strong></p>
  <ul>
    <li>
      <strong>Hosting Provider:</strong> Cloudflare Inc, United States of
      America
    </li>
    <li><strong>Hosting Provider:</strong> Supabase Pte. Ltd., Singapore</li>
    <li>
      <strong>CRM Platform:</strong> Hubspot Inc, United States of America
    </li>
    <li>
      <strong>Web Analytics:</strong> Posthog Inc, United States of America
    </li>
    <li><strong>Error Tracking:</strong> Sentry, United States of America</li>
    <li>
      <strong>Customer Queries:</strong> Linear Obrit Inc, United States of
      America
    </li>
    <li><strong>Payments:</strong> Stripe Payments Europe Limited, Ireland</li>
    <li>
      <strong>User Support:</strong> FrontApp,Inc, United States of America
    </li>
  </ul>
  <p>
    User data shall only be used by MAPPR for purposes strictly necessary to
    properly fulfil the legitimate and declared purposes of the personal data
    processing as outlined in Section 5 of this Privacy Policy.
  </p>
  <h2 id="12-modifications-to-the-privacy-policy">
    12 Modifications to the privacy policy
  </h2>
  <p>
    MAPPR reserves the right to review this Privacy Policy at any time. For this
    reason, we recommend that Users regularly review this Privacy Policy to stay
    informed of the latest version.
  </p>
  <p>
    Nevertheless, any changes made to this Privacy Policy will be communicated
    to the User.
  </p>
  <h2 id="13-links-to-third-party-websites">
    13. Links to Third-Party Websites
  </h2>
  <p>
    The Website may include links to third-party websites, platforms, or
    organisations. MAPPR is not responsible for how those third parties handle
    the privacy or processing of personal data.
  </p>
  <p>
    Therefore, we recommend that Users carefully review the privacy policies and
    terms of use of any external websites accessed via links from the Website,
    as their conditions may differ from those set by MAPPR
  </p>
  <p>
    The inclusion of such links does not imply approval, oversight, or
    responsibility by MAPPR for the content, services, or privacy policies of
    the linked pages.
  </p>
  <h2 id="14-questions">14. Questions</h2>
  <p>
    If you have any questions about our Privacy Policy, please contact us by
    sending an email to: <a href="mailto:info@mappr.com">info@mappr.com</a>
  </p>
  <p>
    Likewise, if you believe your rights have not been adequately safeguarded,
    you may lodge a complaint with the Spanish Data Protection Agency (AEPD).
    Contact details:
  </p>
  <ul>
    <li>
      Phone: 900 293 183 o <a href="tel:+34900293621">900 293 621</a> (Education
      and Minors’ Data Protection)
    </li>
    <li>Address: St. Jorge Juan 6, zip: 28001 Madrid (Spain)</li>
    <li>
      Electronic Office:
      <a
        href="https://sedeaepd.gob.es/sede-electronica-web/"
        target="_blank"
        class="url"
        >https://sedeaepd.gob.es/sede-electronica-web/</a
      >
    </li>
    <li>
      Online Form:
      <a href="https://www.aepd.es/en" target="_blank" class="url"
        >https://www.aepd.es/en</a
      >
    </li>
  </ul>
  <h2 id="15-acceptance">15. Acceptance</h2>
  <p>
    The User confirms that they have been informed of the terms governing the
    processing of their personal data and, by accepting this Privacy Policy,
    expressly consents to the processing of such data by MAPPR in accordance
    with the purposes and conditions described herein.
  </p>
  <p>
    2025 “MAPPREXPLORE, S.L.” Reproduction in whole or in part is prohibited.
    All rights reserved.
  </p>
</template>
<style lang="postcss" scoped>
a {
  color: var(--color-indigo-500);
}
</style>
